import { useEffect, useRef } from 'react';
import { useAuth } from '@/auth/useAuth';
import {
  refreshAttention,
  reprobeDisabledAttention,
  resetAttention,
  setAttentionEligibility,
  useAttentionStore,
} from './attentionStore';

export const POLL_MS = 60_000;
export const FOCUS_THROTTLE_MS = 15_000;

/** Single place naming the admin sources module, so it stays a dynamic import. */
const loadAdminSources = () => import('./sources/admin');

/** Newest fetch time across all sources, 0 when nothing has been fetched. */
function lastFetchedAt(): number {
  let newest = 0;
  for (const s of Object.values(useAttentionStore.getState().states)) {
    if (s.fetchedAt !== undefined && s.fetchedAt > newest) newest = s.fetchedAt;
  }
  return newest;
}

const isVisible = () => document.visibilityState !== 'hidden';

/**
 * Background poller for admin attention badges. Renders nothing and holds no
 * React state. Only authenticated admins ever import the admin sources or
 * fetch; it never touches the session keep-alive (no holdActive/notifyActivity).
 */
export function AttentionPoller(): null {
  const { authenticated, hasRole } = useAuth();
  const eligible = authenticated && hasRole('admin');

  const hasRoleRef = useRef(hasRole);
  useEffect(() => {
    hasRoleRef.current = hasRole;
  });

  useEffect(() => {
    if (!eligible) {
      resetAttention();
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    // When this poller last STARTED a refresh. `lastFetchedAt()` only moves
    // when a fetch settles, so throttling on it alone lets the focus event
    // that follows a visibilitychange through while the first fetch is still
    // in flight, and the store then discards that answer and fetches again.
    let lastKickAt = 0;

    const kick = () => {
      lastKickAt = Date.now();
      void refreshAttention();
    };

    const stopTimer = () => {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    };
    const startTimer = () => {
      stopTimer();
      timer = setInterval(kick, POLL_MS);
    };
    const refreshIfStale = () => {
      const last = Math.max(lastKickAt, lastFetchedAt());
      if (Date.now() - last >= FOCUS_THROTTLE_MS) kick();
    };
    const onVisibility = () => {
      if (isVisible()) {
        // A tab return gives disabled sources one more try (spec §6.4).
        reprobeDisabledAttention();
        refreshIfStale();
        startTimer();
      } else {
        stopTimer();
      }
    };
    const onFocus = () => {
      if (isVisible()) refreshIfStale();
    };

    // Order matters: eligibility first, then the sources, then the first fetch.
    setAttentionEligibility((role) => hasRoleRef.current(role));
    loadAdminSources()
      .then(() => {
        if (cancelled) return;
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        if (isVisible()) {
          kick();
          startTimer();
        }
      })
      .catch((err: unknown) => {
        // Never toast or console.error for a background failure (A3). The
        // poller stays idle and retries on the next activation.
        console.debug('attention: admin sources failed to load', err);
      });

    return () => {
      cancelled = true;
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      // Deactivation (role lost, logout, or layout teardown): drop admin
      // counts and revoke eligibility so nothing stale survives.
      resetAttention();
    };
  }, [eligible]);

  return null;
}

export default AttentionPoller;
